import { keepPreviousData, useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthenticatedClient, queryKeys } from './queries'
import { useAuth } from '@/hooks/use-auth'
import { POLL_ANALYSIS, POLL_NORMAL, POLL_SLOW } from './polling'
import type { components } from './schema'
import type { ServiceTier } from '@/lib/service-tier'
import { invalidateWarrantyClaims } from '@/features/warranty-claims/api'
import { responseError } from '@/lib/api-error'

// Base URL for raw fetch() calls (multipart uploads etc. that don't go through openapi-fetch).
const DOCS_API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
const USE_MOCK_AP_INVOICES = import.meta.env.VITE_MOCK_AP_INVOICES === '1'
const MOCK_CURRENT_USER: components['schemas']['UserRead'] = {
  id: 1001,
  uuid: 'staff-lesley-ap',
  user_id: 'lesley.ap@shopusars.com',
  email: 'lesley.ap@shopusars.com',
  name: 'Lesley AP',
  role: 'accounting',
}

// ---- Shared types for paginated params ----
export interface PaginatedParams {
  page?: number
  limit?: number
  search?: string
  sort_by?: string
  sort_dir?: string
}

// ---- Current User ----

export function useCurrentUser() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.staff.me,
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) return MOCK_CURRENT_USER
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/staff/me')
      if (error) throw error
      return data
    },
    enabled: !USE_MOCK_AP_INVOICES,
    initialData: USE_MOCK_AP_INVOICES ? MOCK_CURRENT_USER : undefined,
    staleTime: Infinity,
  })
}

// ---- Dashboard Summary ----

interface DashboardSummaryParams {
  ops_person_id?: number
}

export function useDashboardSummary(params?: DashboardSummaryParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.dashboardSummary.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/summary', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

// ---- Projects ----

interface ProjectParams extends PaginatedParams {
  status?: string
  stage?: string
  customer_group_id?: number
  manufacturer_uuid?: string
  ops_person_id?: number
  staff_id?: number
  tracking_enabled?: boolean
  source_type?: 'shopify' | 'dropship'
  priority?: ProjectPriority
  service_tier?: ServiceTier[]
}

export type ProjectPriority = components['schemas']['InternalProjectRead']['priority']
export type ActionPriorityUpdate = components['schemas']['ActionPriorityUpdate']

export interface ProjectApCostInvoice {
  invoice_id: number
  invoice_uuid: string
  invoice_number?: string | null
  vendor_name?: string | null
  status: string
  currency: string
  expense_category?: string | null
  po_backed: boolean
  invoice_amount: string | number
  approved_amount: string | number
  cash_paid_amount: string | number
  credit_applied_amount: string | number
  held_disputed_amount: string | number
  expected_po_amount?: string | number | null
  known_variance?: string | number | null
  allocation_required: boolean
  dashboard_path: string
}

export interface ProjectCostLink {
  invoice_uuid: string
  invoice_number?: string | null
  vendor_name?: string | null
  status: string
  expense_category?: string | null
  amount: string | number
  dashboard_path: string
}

export interface ProjectFreightShipment {
  shipment_uuid: string
  purchase_order_uuid?: string | null
  po_number?: string | null
  carrier_name?: string | null
  pro_number?: string | null
  booked_amount?: string | number | null
  invoiced_amount?: string | number | null
  cost_amount: string | number
  invoices: ProjectCostLink[]
}

export interface ProjectFreightCosts {
  quoted_amount: string | number
  booked_amount: string | number
  invoiced_amount: string | number
  cost_amount: string | number
  awaiting_invoice_count: number
  shipments: ProjectFreightShipment[]
  project_invoices: ProjectCostLink[]
}

export interface ProjectApCostSummary {
  project_id: number
  project_uuid: string
  currency: string
  committed_po_amount: string | number
  invoiced_amount: string | number
  approved_amount: string | number
  cash_paid_amount: string | number
  credit_applied_amount: string | number
  open_credit_amount: string | number
  held_disputed_amount: string | number
  known_variance: string | number
  po_backed_invoice_amount: string | number
  non_po_invoice_amount: string | number
  allocation_required_count: number
  variance_review_required_count: number
  excluded_non_usd_count: number
  warnings: string[]
  invoices: ProjectApCostInvoice[]
  freight: ProjectFreightCosts
  install_amount: string | number
  storage_amount: string | number
  install_storage_invoices: ProjectCostLink[]
  additional_net_amount: string | number
}

export function useProjects(params?: ProjectParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useProject(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function useProjectApCostSummary(uuid: string) {
  const { getAccessToken } = useAuth()
  return useQuery({
    queryKey: queryKeys.projects.apCosts(uuid),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          project_id: 1,
          project_uuid: uuid,
          currency: 'USD',
          committed_po_amount: 0,
          invoiced_amount: 0,
          approved_amount: 0,
          cash_paid_amount: 0,
          credit_applied_amount: 0,
          open_credit_amount: 0,
          held_disputed_amount: 0,
          known_variance: 0,
          po_backed_invoice_amount: 0,
          non_po_invoice_amount: 0,
          allocation_required_count: 0,
          variance_review_required_count: 0,
          excluded_non_usd_count: 0,
          warnings: [],
          invoices: [],
          freight: {
            quoted_amount: 0,
            booked_amount: 0,
            invoiced_amount: 0,
            cost_amount: 0,
            awaiting_invoice_count: 0,
            shipments: [],
            project_invoices: [],
          },
          install_amount: 0,
          storage_amount: 0,
          install_storage_invoices: [],
          additional_net_amount: 0,
        } satisfies ProjectApCostSummary
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      const response = await fetch(
        `${DOCS_API_BASE_URL}/dashboard/projects/${encodeURIComponent(uuid)}/ap-cost-summary`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!response.ok) {
        throw await responseError(response, `Project AP costs request failed with ${response.status}`)
      }
      return (await response.json()) as ProjectApCostSummary
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

interface ProjectTimelineParams {
  event_category?: string
  event_type?: string
  limit?: number
}

export function useProjectTimeline(uuid: string, params?: ProjectTimelineParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.timeline(uuid, params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/timeline', {
        params: { path: { uuid }, query: params },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

interface GlobalTimelineParams {
  event_category?: string
  event_type?: string
  ops_person_id?: number
  limit?: number
}

export function useGlobalTimeline(params?: GlobalTimelineParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.globalTimeline(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/timeline', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useProjectActions(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.actions(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/actions', {
        params: { path: { uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!projectUuid,
    refetchInterval: POLL_NORMAL,
  })
}

/** Each PO's planned and expected ship and arrival against the customer's date (USA-1329). */
export function useProjectDeliveryTimeline(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.deliveryTimeline(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/delivery-timeline', {
        params: { path: { uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!projectUuid,
  })
}

/** Project uuid -> its PO timelines, for one page of the projects table (USA-1498). */
export function useProjectsDeliveryTimelines(projectUuids: string[]) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.deliveryTimelines(projectUuids),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/delivery-timelines', {
        params: { query: { uuid: projectUuids } },
      })
      if (error) throw error
      return new Map(data.map((project) => [project.project_uuid, project.timelines]))
    },
    enabled: projectUuids.length > 0,
  })
}

export function useResolveProjectAction(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ poUuid, actionId, status, notes }: { poUuid: string; actionId: number; status: string; notes?: string }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/purchase-orders/{uuid}/actions/{action_id}', {
        params: { path: { uuid: poUuid, action_id: actionId } },
        body: { status, notes },
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.actions(projectUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(projectUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.globalActions })
    },
  })
}

interface ProjectsWithActionsParams extends PaginatedParams {
  ops_person_id?: number
  staff_id?: number
  assigned_role?: string
  project_priority?: ProjectPriority
  service_tier?: ServiceTier[]
  /** Someone is working it (in_progress) or no one has started it (not_started); both when unset.
   * Closed items instead, the latest closed first (resolved, USA-1653). */
  progress?: 'not_started' | 'in_progress' | 'resolved'
  priority?: 'high' | 'normal' | 'low'
  action_type?: string
}

type ActionsWithoutProjectParams = Omit<ProjectsWithActionsParams, 'page' | 'ops_person_id' | 'project_priority' | 'service_tier'>

/** Accounting's invoice items whose invoice has no project: the Action Items "No project" group (USA-1184). */
export function useActionsWithoutProject(params: ActionsWithoutProjectParams, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.withoutProjectActions(params),
    enabled,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/without-project-actions', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
    refetchInterval: POLL_NORMAL,
  })
}

/** Kinds of action item with open items, by their short name, for the Action Items type filter (USA-1443). */
export function useActionTypeOptions() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.actionTypes,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/action-types')
      if (error) throw error
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useProjectsWithActions(params?: ProjectsWithActionsParams, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.withActions(params),
    enabled,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/with-actions', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useProjectCancellationPreview(projectUuid: string, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.cancellationPreview(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/cancellation-preview', {
        params: { path: { uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: enabled && !!projectUuid,
    staleTime: 0,
  })
}

export function useCancelProject(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  // Not optimistic: the dialog stays open on "Cancelling..." until Shopify and the server confirm.
  return useMutation({
    mutationFn: async (reason: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/projects/{uuid}/cancel', {
        params: { path: { uuid: projectUuid } },
        body: { reason },
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useCompleteCancellationAction(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/projects/{uuid}/cancellation-action/complete',
        {
          params: { path: { uuid: projectUuid } },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projects.all })
      const previous = queryClient.getQueriesData({
        queryKey: queryKeys.projects.all,
      })
      queryClient.setQueriesData<
        components['schemas']['ActionGroupsResponse_InternalProjectWithActionsRead_']
      >({ queryKey: ['projects', 'with-actions'] }, (data) =>
        data
          ? {
              ...data,
              items: data.items.map((project) =>
                project.uuid === projectUuid
                  ? {
                      ...project,
                      actions: project.actions.filter(
                        (action) => action.action_source !== 'project_cancellation',
                      ),
                      open_action_count: Math.max(0, (project.open_action_count ?? 1) - 1),
                    }
                  : project,
              ),
            }
          : data,
      )
      queryClient.setQueryData<components['schemas']['ProjectOpenActionRead'][]>(
        queryKeys.projects.actions(projectUuid),
        (actions) => actions?.filter((action) => action.action_source !== 'project_cancellation'),
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

export function useUpdateOrderLineAction(orderUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      body,
    }: {
      actionId: number
      body: components['schemas']['OrderLineActionUpdate']
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/orders/{uuid}/line-actions/{action_id}',
        {
          params: { path: { uuid: orderUuid, action_id: actionId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderUuid) })
    },
  })
}

export function useCreateOrderLinePurchaseOrder(orderUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      actionId,
      body,
    }: {
      actionId: number
      body: components['schemas']['OrderLinePurchaseOrderCreate']
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/orders/{uuid}/line-actions/{action_id}/purchase-order',
        {
          params: { path: { uuid: orderUuid, action_id: actionId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export function useGlobalOpenActions(params?: { ops_person_id?: number; staff_id?: number; assigned_role?: string }) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.purchaseOrders.globalActions, params] as const,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/purchase-orders/actions', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useResolveGlobalAction() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ poUuid, actionId, status, notes }: { poUuid: string; actionId: number; status: string; notes?: string }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/purchase-orders/{uuid}/actions/{action_id}', {
        params: { path: { uuid: poUuid, action_id: actionId } },
        body: { status, notes },
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.globalActions })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: ['projects', 'with-actions'] })
    },
  })
}

export function useCreateProject() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; status?: string; description?: string; notes?: string; customer_group_id?: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/projects', {
        body: { ...body, status: body.status ?? 'active' },
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

async function dropshipUploadError(resp: Response): Promise<string> {
  const fallback = 'Upload failed. Check the AutoQuotes .aqproj export and try again.'
  const contentType = resp.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    const body: unknown = await resp.json().catch(() => null)
    if (body && typeof body === 'object' && 'detail' in body) {
      const detail = (body as { detail?: unknown }).detail
      if (typeof detail === 'string' && detail.trim()) {
        const lower = detail.toLowerCase()
        if (lower.includes('empty')) return 'Choose a non-empty AutoQuotes .aqproj export before uploading.'
        if (lower.includes('exceed') || lower.includes('too large')) return 'This quote is over the upload limit.'
        return detail
      }
    }
    return fallback
  }
  return fallback
}

export type DropshipQuoteDraft = components['schemas']['DropshipQuoteDraftRead']
export type DropshipQuoteDraftSummary = components['schemas']['DropshipQuoteDraftSummaryRead']
export type DropshipQuoteDraftList = components['schemas']['DropshipQuoteDraftListRead']
export type DropshipQuoteDraftEvent = components['schemas']['DropshipQuoteDraftEventRead']
export type DropshipQuoteDraftPatch = components['schemas']['DropshipQuoteDraftPatch']
export type DropshipQuoteDraftLineItemCreate =
  components['schemas']['DropshipQuoteDraftLineItemCreate']
export type DropshipQuoteDraftConfirmRequest = components['schemas']['DropshipQuoteDraftConfirmRequest']
export type DropshipQuoteDraftScratchCreate = components['schemas']['DropshipQuoteDraftScratchCreate']

export type DropshipQuoteDraftListParams = {
  status?: string | null
  default_open?: boolean
  search?: string | null
  source_origin?: string | null
  file_name?: string | null
  sort?: 'recency' | '-updated_at' | 'updated_at' | '-created_at' | 'created_at'
  limit?: number
  offset?: number
}

export function useDropshipQuoteDrafts(params: DropshipQuoteDraftListParams = {}) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.dropshipQuoteDrafts.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/dropship-intake/drafts', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useCreateDropshipQuoteDraft() {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')

      const formData = new FormData()
      formData.append('file', file)
      formData.append('project_type', 'dropship')
      formData.append('source_origin', 'upload')

      const resp = await fetch(`${DOCS_API_BASE_URL}/dashboard/dropship-intake/drafts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!resp.ok) throw new Error(await dropshipUploadError(resp))
      return (await resp.json()) as DropshipQuoteDraft
    },
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.detail(draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

export function useCreateScratchDropshipQuoteDraft() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: DropshipQuoteDraftScratchCreate) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/dropship-intake/drafts/scratch', {
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.detail(draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

export function useDropshipQuoteDraft(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.dropshipQuoteDrafts.detail(draftUuid ?? ''),
    queryFn: async () => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/dropship-intake/drafts/{draft_uuid}', {
        params: { path: { draft_uuid: draftUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!draftUuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function useDropshipQuoteDraftEvents(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.dropshipQuoteDrafts.events(draftUuid ?? ''),
    queryFn: async () => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/dropship-intake/drafts/{draft_uuid}/events', {
        params: { path: { draft_uuid: draftUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!draftUuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function usePatchDropshipQuoteDraft(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: DropshipQuoteDraftPatch) => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/dropship-intake/drafts/{draft_uuid}', {
        params: { path: { draft_uuid: draftUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.detail(draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.events(draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

export function useValidateDropshipQuoteDraft(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/dropship-intake/drafts/{draft_uuid}/validate', {
        params: { path: { draft_uuid: draftUuid } },
      })
      if (error) throw error
      return data
    },
    onSuccess: (validation) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.detail(validation.draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.events(validation.draft.uuid) })
    },
  })
}

export function useConfirmDropshipQuoteDraft(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: DropshipQuoteDraftConfirmRequest) => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/dropship-intake/drafts/{draft_uuid}/confirm', {
        params: { path: { draft_uuid: draftUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.detail(draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.events(draft.uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

function removeDropshipDraftLineOptimistically(
  draft: DropshipQuoteDraft,
  lineIndex: number,
): DropshipQuoteDraft {
  const draftFields = { ...(draft.draft_fields ?? {}) }
  const rawLineItems = draftFields['quote.line_items']
  if (Array.isArray(rawLineItems)) {
    draftFields['quote.line_items'] = rawLineItems
      .filter((_, index) => index !== lineIndex)
      .map((line, index) =>
        line && typeof line === 'object' && !Array.isArray(line)
          ? { ...line, line_number: index + 1 }
          : line,
      )
  }

  const linkedLineItems = draft.linked_line_items
    .filter((link) => link.line_index !== lineIndex)
    .map((link) => ({
      ...link,
      line_index: link.line_index > lineIndex ? link.line_index - 1 : link.line_index,
      line_number:
        link.line_number && link.line_index > lineIndex ? link.line_number - 1 : link.line_number,
    }))

  return { ...draft, draft_fields: draftFields, linked_line_items: linkedLineItems }
}

export function useAddDropshipQuoteDraftLineItem(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.dropshipQuoteDrafts.detail(draftUuid ?? '')
  return useMutation({
    mutationFn: async (body: DropshipQuoteDraftLineItemCreate) => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/dropship-intake/drafts/{draft_uuid}/line-items',
        {
          params: { path: { draft_uuid: draftUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(detailKey, draft)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      if (draftUuid) {
        queryClient.invalidateQueries({ queryKey: detailKey })
        queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.events(draftUuid) })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

export function useDeleteDropshipQuoteDraftLineItem(draftUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.dropshipQuoteDrafts.detail(draftUuid ?? '')
  return useMutation({
    mutationFn: async ({ lineIndex }: { lineIndex: number }) => {
      if (!draftUuid) throw new Error('Missing draft id')
      const client = await getClient()
      const { data, error } = await client.DELETE(
        '/dashboard/dropship-intake/drafts/{draft_uuid}/line-items/{line_index}',
        {
          params: { path: { draft_uuid: draftUuid, line_index: lineIndex } },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ lineIndex }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<DropshipQuoteDraft>(detailKey)
      queryClient.setQueryData<DropshipQuoteDraft>(detailKey, (old) =>
        old ? removeDropshipDraftLineOptimistically(old, lineIndex) : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(detailKey, draft)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.all })
      if (draftUuid) {
        queryClient.invalidateQueries({ queryKey: detailKey })
        queryClient.invalidateQueries({ queryKey: queryKeys.dropshipQuoteDrafts.events(draftUuid) })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

export function useProjectAutomationHistory(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.automationHistory(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/automation-history', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

export function useUpdateProject(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.projects.detail(uuid)
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/projects/{uuid}', {
        params: { path: { uuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: body as any,
      })
      if (error) throw error
      return data
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      queryClient.setQueryData(detailKey, (old: Record<string, unknown> | undefined) =>
        old ? { ...old, ...body } : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: (_data, _err, body) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: detailKey })
      // A project's deliver-by date is every PO's (USA-1475): their timelines change with it.
      if ('need_by' in body) queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

/**
 * Pick a project's service tier (USA-455). The tier moves the project's priority to its default, and
 * every PO and action item list shows or filters by it, so they all refresh.
 */
export function useSetProjectServiceTier(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (tier: ServiceTier) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/projects/{uuid}/service-tier', {
        params: { path: { uuid } },
        body: { tier },
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

/** Pick an action item's priority (USA-1451); every list and card that shows it refreshes. */
export function useSetActionPriority() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ActionPriorityUpdate) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/action-priorities', { body })
      if (error) throw error
      return data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
      // Warranty items list on their claims (USA-915).
      invalidateWarrantyClaims(queryClient)
    },
  })
}

export type ManualStatusCorrectionBody = components['schemas']['ManualStatusCorrectionRequest']
export type ShipmentStatusCorrectionBody = components['schemas']['ShipmentStatusCorrectionRequest']

function withOptimisticStatus<
  T extends { status: string; status_revision: number; status_source: string },
>(entity: T, body: ManualStatusCorrectionBody): T {
  return {
    ...entity,
    status: body.status,
    status_revision: body.expected_revision + 1,
    status_source: 'manual',
  }
}

function withOptimisticShipment<T extends { status: string; status_revision: number; status_source: string }>(
  shipment: T,
  body: ShipmentStatusCorrectionBody,
): T {
  return {
    ...withOptimisticStatus(shipment, body),
    ...(body.estimated_ship_date !== undefined && { estimated_ship_date: body.estimated_ship_date }),
    ...(body.carrier_name !== undefined && { carrier_name: body.carrier_name }),
    ...(body.tracking_numbers !== undefined && { tracking_numbers: body.tracking_numbers }),
    ...(body.pro_number !== undefined && { pro_number: body.pro_number }),
    ...(body.bol_number !== undefined && { bol_number: body.bol_number }),
    ...(body.pickup_number !== undefined && { pickup_number: body.pickup_number }),
    ...(body.bol_reference !== undefined && { bol_reference: body.bol_reference }),
  }
}

function useStatusCorrectionInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })
    queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
  }
}

export function useCorrectProjectStatus(uuid: string) {
  const getClient = useAuthenticatedClient()
  const invalidate = useStatusCorrectionInvalidation()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.projects.detail(uuid)
  return useMutation({
    mutationFn: async (body: ManualStatusCorrectionBody) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/projects/{uuid}/status-correction', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<components['schemas']['InternalProjectDetailRead']>(detailKey)
      queryClient.setQueryData<components['schemas']['InternalProjectDetailRead']>(detailKey, (current) =>
        current ? withOptimisticStatus(current, body) : current,
      )
      return { previous }
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: invalidate,
  })
}

export function useDeleteProject() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/projects/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
  })
}

// ---- Project Share Links ----

export type ProjectShareLink = components['schemas']['ProjectShareLinkRead']

export function useProjectShareLinks(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projects.shareLinks(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/share-links', {
        params: { path: { uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!projectUuid,
  })
}

export function useCreateProjectShareLink(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { label?: string | null; expires_at?: string | null }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/projects/{uuid}/share-links', {
        params: { path: { uuid: projectUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.shareLinks(projectUuid) })
    },
  })
}

export function useUpdateProjectShareLink(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      linkUuid,
      body,
    }: {
      linkUuid: string
      body: { label?: string | null; is_active?: boolean; expires_at?: string | null }
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/projects/{uuid}/share-links/{link_uuid}',
        {
          params: { path: { uuid: projectUuid, link_uuid: linkUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.shareLinks(projectUuid) })
    },
  })
}

export function useDeleteProjectShareLink(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (linkUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE(
        '/dashboard/projects/{uuid}/share-links/{link_uuid}',
        {
          params: { path: { uuid: projectUuid, link_uuid: linkUuid } },
        },
      )
      if (error) throw error
    },
    onMutate: async (linkUuid) => {
      const key = queryKeys.projects.shareLinks(projectUuid)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ProjectShareLink[]>(key)
      queryClient.setQueryData<ProjectShareLink[]>(key, (old) =>
        old ? old.filter((link) => link.uuid !== linkUuid) : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.projects.shareLinks(projectUuid), context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.shareLinks(projectUuid) })
    },
  })
}

// ---- Orders ----

interface OrderParams extends PaginatedParams {
  status?: string
  project_id?: number
  ops_person_id?: number
}

export function useOrders(params?: OrderParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.orders.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/orders', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useOrder(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.orders.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/orders/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function useCreateOrder() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/orders', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { status: 'pending', currency: 'USD', ...body } as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })
    },
  })
}

export function useUpdateOrder(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/orders/{uuid}', {
        params: { path: { uuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: body as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(uuid) })
    },
  })
}

export function useCorrectOrderPartStatus(uuid: string) {
  const getClient = useAuthenticatedClient()
  const invalidate = useStatusCorrectionInvalidation()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.orders.detail(uuid)
  return useMutation({
    mutationFn: async ({
      lineItemId,
      body,
    }: {
      lineItemId: number
      body: ManualStatusCorrectionBody
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/orders/{uuid}/line-items/{line_item_id}/status-correction',
        { params: { path: { uuid, line_item_id: lineItemId } }, body },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ lineItemId, body }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<components['schemas']['InternalOrderDetailRead']>(detailKey)
      queryClient.setQueryData<components['schemas']['InternalOrderDetailRead']>(detailKey, (current) =>
        current
          ? {
              ...current,
              line_items: current.line_items.map((item) =>
                item.id === lineItemId ? withOptimisticStatus(item, body) : item,
              ),
            }
          : current,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: invalidate,
  })
}

// ---- Purchase Orders ----

// Same-object exports for existing consumers; PO screens use the feature module directly.
export {
  usePurchaseOrders,
  usePurchaseOrder,
  usePurchaseOrderDetails,
  usePurchaseOrderAutomationHistory,
  useUpdatePurchaseOrder,
  useHoldPurchaseOrder,
  useReleasePurchaseOrderHold,
  useUpdateInvoiceExpectation,
  useUpdateLineItem,
  useAddLineItem,
  useDeleteLineItem,
  useResolveAction,
  useResolveDeliverySite,
  useCreateAction,
  useRemoveActionItem,
  useReplaceActionItem,
  useApplyDiscountChainToAll,
  useUpdateDiscountAction,
  useReplacementCandidates,
  useAcceptFee,
  useApproveBackorder,
  useSendCustomerNotification,
  useDraftCustomerEmail,
  useSkipCustomerApproval,
  useSendDraftResponse,
  useSkipSpecCredit,
} from '@/features/purchase-orders/hooks'

export function useCorrectPurchaseOrderStatus(uuid: string) {
  const getClient = useAuthenticatedClient()
  const invalidate = useStatusCorrectionInvalidation()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.purchaseOrders.detail(uuid)
  return useMutation({
    mutationFn: async (body: ManualStatusCorrectionBody) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/purchase-orders/{uuid}/status-correction',
        { params: { path: { uuid } }, body },
      )
      if (error) throw error
      return data
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey)
      queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(detailKey, (current) =>
        current ? withOptimisticStatus(current, body) : current,
      )
      return { previous }
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: invalidate,
  })
}

/** Set one status on several POs at once (USA-1202); a PO changed meanwhile stops the whole set. */
export function useBulkCorrectPurchaseOrderStatus() {
  const getClient = useAuthenticatedClient()
  const invalidate = useStatusCorrectionInvalidation()
  return useMutation({
    mutationFn: async (body: components['schemas']['BulkPOStatusCorrection']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/purchase-orders/bulk-status-correction', {
        body,
      })
      if (error) throw error
      return data
    },
    onSettled: invalidate,
  })
}

// ---- Shipments ----

interface ShipmentParams extends PaginatedParams {
  status?: string
  booked_by?: string
  ops_person_id?: number
}

export function useShipments(params?: ShipmentParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.shipments.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipments', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useShipment(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.shipments.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipments/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

// ---- Customer Groups ----

interface CustomerGroupParams extends PaginatedParams {
  ops_person_id?: number
}

export function useCustomerGroups(params?: CustomerGroupParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerGroups.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/customer-groups', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

export function useCustomerGroup(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerGroups.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/customer-groups/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

export function useCreateCustomerGroup() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; company_name?: string; email?: string; phone?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/customer-groups', {
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerGroups.all })
    },
  })
}

export function useUpdateCustomerGroup(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/customer-groups/{uuid}', {
        params: { path: { uuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: body as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerGroups.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.customerGroups.detail(uuid) })
    },
  })
}

export function useAddGroupMember(groupUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { shopify_customer_id: number; role?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/customer-groups/{uuid}/members', {
        params: { path: { uuid: groupUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerGroups.detail(groupUuid) })
    },
  })
}

export function useRemoveGroupMember(groupUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.customerGroups.detail(groupUuid)
  return useMutation({
    mutationFn: async (memberId: number) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/customer-groups/{uuid}/members/{member_id}', {
        params: { path: { uuid: groupUuid, member_id: memberId } },
      })
      if (error) throw error
    },
    onMutate: async (memberId) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<components['schemas']['CustomerGroupDetailRead']>(detailKey)
      queryClient.setQueryData<components['schemas']['CustomerGroupDetailRead']>(detailKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.filter((member) => member.id !== memberId),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

// ---- Customers (Shopify, read-only) ----

export function useCustomers(params?: PaginatedParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customers.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/customers', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

// ---- Manufacturers ----

interface ManufacturerParams extends PaginatedParams {
  status?: string
  tracking?: 'quoting' | 'storefront' | 'quoting_only' | 'untracked'
  /** A supplier's UUID, or 'direct' (USA-1535). */
  source?: string
  terms?: 'prepaid' | 'deposit' | 'due_on_receipt' | 'net' | 'discount_net' | 'proximo' | 'none'
}

export function useManufacturers(params?: ManufacturerParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.manufacturers.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturers', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

export type CompetitorListKind = 'below-floor' | 'below-map'

export function useManufacturerCompetitorPrices(
  uuid: string,
  kind: CompetitorListKind,
  page: number,
  limit = 25,
) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.manufacturers.competitorPrices(uuid, kind, page),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/manufacturers/{uuid}/competitor-prices/{kind}',
        { params: { path: { uuid, kind }, query: { page, limit } } },
      )
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
  })
}

export function useManufacturer(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.manufacturers.detail(uuid),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES && uuid === 'manu-bk-resources') {
        return {
          id: 12,
          uuid,
          source: 'mock',
          source_id: 'manu-bk-resources',
          name: 'BK Resources',
          short_name: 'BK Resources',
          state: 'NY',
          zip_code: '10001',
          skip_spec_credit: false,
          skip_po_email: false,
          custom_pricing: false,
          safeware: false,
          ships_products: true,
          ships_to_residential: true,
          freight_booking: 'auto' as const,
          free_shipping: false,
          quote_tracking: false,
          aliases: [],
          created_at: '2026-06-01T00:00:00Z',
          updated_at: '2026-06-20T00:00:00Z',
          media: [],
          contact_links: [],
          sourcing_options: [],
          special_programs: [],
          discounts: [],
          freight_rules: [],
          fee_rules: [],
          map_policy: null,
          ap_invoices: [
            {
              id: 9001,
              uuid: 'inv-demo-9001',
              invoice_number: 'SO207465',
              invoice_date: '2026-06-03',
              official_due_date: '2026-07-03',
              priority_due_date: '2026-06-24',
              invoice_total: '24068.80',
              status: 'needs_approval',
              priority_status: 'pay_now',
              duplicate_status: 'possible_duplicate',
              vendor: { id: 44, uuid: 'vendor-bk-resources', name: 'BK Resources', entity_type: 'manufacturer' },
              vendor_raw: 'BK Resources',
              internal_project: { id: 501, uuid: 'project-demo-kec', name: 'Kitchen Equipment Contractors', tracking_enabled: true },
              project_ship_to_raw: 'Kitchen Equipment Contractors',
              po_links: [
                {
                  id: 1,
                  purchase_order: { id: 101, uuid: 'po-demo-bkr-1', po_number: 'KZ316262BKR', status: 'sent', manufacturer_id: 12 },
                  po_number_raw: 'KZ316262BKR',
                },
              ],
            },
          ],
          purchase_orders: [],
          active_product_count: 0,
          store_pending_product_count: 0,
        }
      }
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturers/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

export function useAddManufacturerContact(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { email: string; name?: string; role: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/manufacturers/{uuid}/contacts', {
        params: { path: { uuid: manuUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useUpdateManufacturerContact(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      linkId,
      ...body
    }: { linkId: number } & components['schemas']['ManufacturerContactLinkUpdate']) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/manufacturers/{uuid}/contacts/{link_id}', {
        params: { path: { uuid: manuUuid, link_id: linkId } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (link) => {
      queryClient.setQueryData<components['schemas']['ManufacturerDetailRead']>(
        queryKeys.manufacturers.detail(manuUuid),
        (old) => old && {
          ...old,
          contact_links: old.contact_links?.map((item) => item.id === link.id ? link : item),
        },
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all })
    },
  })
}

export function useRemoveManufacturerContact(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(manuUuid)
  return useMutation({
    mutationFn: async (linkId: number) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/manufacturers/{uuid}/contacts/{link_id}', {
        params: { path: { uuid: manuUuid, link_id: linkId } },
      })
      if (error) throw error
    },
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<components['schemas']['ManufacturerDetailRead']>(detailKey)
      queryClient.setQueryData<components['schemas']['ManufacturerDetailRead']>(detailKey, (old) =>
        old
          ? {
              ...old,
              contact_links: old.contact_links?.filter((contactLink) => contactLink.id !== linkId),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

type FreightRuleCreateBody = {
  rule_type: string
  ff_threshold_net?: string | null
  below_threshold_percent?: string | null
  origin_zip_codes?: string | null
  priority?: number
  effective_from?: string | null
  effective_to?: string | null
  notes?: string | null
  enabled: boolean
  conditions?: components['schemas']['FreightRuleConditionsWrite'] | null
}

export function useCreateManufacturerFreightRule(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(manuUuid)
  return useMutation({
    mutationFn: async (body: FreightRuleCreateBody) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/manufacturers/{uuid}/freight-rules',
        {
          params: { path: { uuid: manuUuid } },
          body: { ...body, priority: body.priority ?? 0 },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useUpdateManufacturerFreightRule(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(manuUuid)
  return useMutation({
    mutationFn: async ({ ruleId, body }: { ruleId: number; body: Partial<FreightRuleCreateBody> }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/manufacturers/{uuid}/freight-rules/{rule_id}',
        {
          params: { path: { uuid: manuUuid, rule_id: ruleId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useDeleteManufacturerFreightRule(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(manuUuid)
  return useMutation({
    mutationFn: async (ruleId: number) => {
      const client = await getClient()
      const { error } = await client.DELETE(
        '/dashboard/manufacturers/{uuid}/freight-rules/{rule_id}',
        {
          params: { path: { uuid: manuUuid, rule_id: ruleId } },
        },
      )
      if (error) throw error
    },
    onMutate: async (ruleId) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(detailKey, (old: any) =>
        old
          ? {
              ...old,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              freight_rules: old.freight_rules?.filter((r: any) => r.id !== ruleId),
            }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export function useUpdateManufacturerDiscount(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(manuUuid)
  return useMutation({
    mutationFn: async ({
      discountId,
      ...body
    }: {
      discountId: number
    } & components['schemas']['ManufacturerDiscountUpdate']) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/manufacturers/{uuid}/discounts/{discount_id}',
        {
          params: { path: { uuid: manuUuid, discount_id: discountId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

/** Every recorded change to the manufacturer's discounts, newest first. */
export function useManufacturerDiscountHistory(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.manufacturers.detail(manuUuid), 'discount-history'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturers/{uuid}/discount-history', {
        params: { path: { uuid: manuUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useManufacturerDiscountCategories(manuUuid: string, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.manufacturers.detail(manuUuid), 'aq-categories'],
    enabled,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/manufacturers/{uuid}/discount-categories',
        {
          params: { path: { uuid: manuUuid } },
        },
      )
      if (error) throw error
      return data
    },
  })
}

export function useManufacturerFreightProductGroups(manuUuid: string, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.manufacturers.detail(manuUuid), 'freight-product-groups'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/manufacturers/{uuid}/freight-product-groups',
        {
          params: { path: { uuid: manuUuid } },
        },
      )
      if (error) throw error
      return data
    },
  })
}

export function useCreateManufacturerDiscount(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['ManufacturerDiscountCreate']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/manufacturers/{uuid}/discounts', {
        params: { path: { uuid: manuUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.manufacturers.detail(manuUuid),
      })
    },
  })
}

export function useDeleteManufacturerDiscount(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(manuUuid)
  type Detail = components['schemas']['ManufacturerDetailRead']
  return useMutation({
    mutationFn: async ({ discountId, note }: { discountId: number; note?: string }) => {
      const client = await getClient()
      const { error } = await client.DELETE(
        '/dashboard/manufacturers/{uuid}/discounts/{discount_id}',
        {
          params: {
            path: { uuid: manuUuid, discount_id: discountId },
            ...(note ? { query: { note } } : {}),
          },
        },
      )
      if (error) throw error
    },
    onMutate: async ({ discountId }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<Detail>(detailKey)
      queryClient.setQueryData<Detail>(
        detailKey,
        (old) =>
          old && {
            ...old,
            discounts: old.discounts.filter((discount) => discount.id !== discountId),
          },
      )
      return { previous }
    },
    onError: (_error, _discountId, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
      toast.error('Could not remove the discount category. Try again.')
    },
    onSuccess: () => toast.success('Discount category removed'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: detailKey }),
  })
}

/** Add a manufacturer AQ does not carry; an existing one with the same name is returned (USA-1100). */
export function useCreateManufacturer() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/manufacturers', { body: { name } })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all })
    },
  })
}

export function useUpdateManufacturer(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(uuid)
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/manufacturers/{uuid}', {
        params: { path: { uuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: body as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      // Terms changes carry to the manufacturer's open POs and their action items.
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
    },
  })
}

export type ManufacturerPaymentMethod = NonNullable<
  components['schemas']['ManufacturerPaymentMethodUpdate']['payment_method']
>

/** Set how we pay the manufacturer: card fees reach accounting only for card suppliers (USA-1470). */
export function useUpdateManufacturerPaymentMethod(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(uuid)
  return useMutation({
    mutationFn: async (paymentMethod: ManufacturerPaymentMethod) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/manufacturers/{uuid}/payment-method', {
        params: { path: { uuid } },
        body: { payment_method: paymentMethod },
      })
      if (error) throw error
      return data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: detailKey }),
  })
}

export type SourceCardFee = components['schemas']['SourceCardFeeRead']
export type SourceCardFeeUpdate = components['schemas']['SourceCardFeeUpdate']

/** Whether paying each of the manufacturer's sources by card adds a fee (USA-1650). */
export function useManufacturerCardFees(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.manufacturers.cardFees(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturers/{uuid}/card-fees', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
  })
}

/** Set a source's card fee; the AI keeps it current from supplier emails (USA-1650). */
export function useUpdateSourceCardFee(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = queryKeys.manufacturers.cardFees(uuid)
  return useMutation({
    mutationFn: async (body: SourceCardFeeUpdate) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/manufacturers/{uuid}/card-fee', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<SourceCardFee[]>(key, (current) =>
        current?.map((fee) =>
          fee.source_name === saved.source_name && fee.through_supplier === saved.through_supplier
            ? { ...saved, sourcing_option_id: fee.sourcing_option_id }
            : fee,
        ),
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}

/** Whether paying an AP invoice by card adds a fee, from its source (USA-1650). */
export function useInvoiceCardFee(invoiceUuid: string | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['ap-invoices', 'card-fee', invoiceUuid] as const,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/ap-invoices/{uuid}/card-fee', {
        params: { path: { uuid: invoiceUuid ?? '' } },
      })
      if (error) throw error
      return data ?? null
    },
    enabled: !!invoiceUuid,
  })
}

/** Set the card fee of an invoice's AP vendor that is linked to no manufacturer or supplier (USA-1650). */
export function useUpdateInvoiceCardFee(invoiceUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = ['ap-invoices', 'card-fee', invoiceUuid] as const
  return useMutation({
    mutationFn: async (body: SourceCardFeeUpdate) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/ap-invoices/{uuid}/card-fee', {
        params: { path: { uuid: invoiceUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (saved) => queryClient.setQueryData(key, saved),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
}

export type ManufacturerFeeRuleWrite = components['schemas']['ManufacturerFeeRuleWrite']

/** Replace the fees a manufacturer charges beyond its small-order rule (USA-1049). */
export function useReplaceManufacturerFeeRules(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(uuid)
  return useMutation({
    mutationFn: async (rules: ManufacturerFeeRuleWrite[]) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/manufacturers/{uuid}/fee-rules', {
        params: { path: { uuid } },
        body: { rules },
      })
      if (error) throw error
      return data
    },
    onSuccess: (rules) => {
      queryClient.setQueryData<components['schemas']['ManufacturerDetailRead']>(
        detailKey,
        (old) => old && { ...old, fee_rules: rules },
      )
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

export type ManufacturerCaseTerms = components['schemas']['ManufacturerCaseTermsRead']
export type ManufacturerCaseTermsUpdate = components['schemas']['ManufacturerCaseTermsUpdate']

/** Set whether case-packed items sell only by the full case, and the broken-case fee (USA-1496). */
export function useUpdateManufacturerCaseTerms(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.manufacturers.detail(uuid)
  return useMutation({
    mutationFn: async (body: ManufacturerCaseTermsUpdate) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/manufacturers/{uuid}/case-terms', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (caseTerms) => {
      queryClient.setQueryData<components['schemas']['ManufacturerDetailRead']>(
        detailKey,
        (old) => old && { ...old, case_terms: caseTerms },
      )
      queryClient.invalidateQueries({ queryKey: detailKey })
    },
  })
}

// ---- MAP policies ----

type MapPolicyCreateBody = {
  policy_type: 'flat_percent' | 'tiered' | 'fixed_price' | 'hybrid'
  enforcement_stage?: 'advertised' | 'sold'
  policy_label?: string
  default_discount_chain?: string | null
  effective_date?: string | null
  expiration_date?: string | null
  status?: 'draft' | 'active' | 'expired'
  list_price_definition?: string | null
  notes?: string | null
}

type MapPolicyPatchBody = Partial<MapPolicyCreateBody>

type TierCreateBody = {
  name: string
  discount_chain: string
  is_default?: boolean
  priority?: number
  notes?: string | null
}

type TierPatchBody = Partial<TierCreateBody>

export function useCreateMapPolicy(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: MapPolicyCreateBody) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/manufacturers/{uuid}/map-policy',
        {
          params: { path: { uuid: manuUuid } },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useUpdateMapPolicy(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ policyUuid, body }: { policyUuid: string; body: MapPolicyPatchBody }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/map-policies/{policy_uuid}',
        {
          params: { path: { policy_uuid: policyUuid } },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useDeleteMapPolicy(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (policyUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/map-policies/{policy_uuid}', {
        params: { path: { policy_uuid: policyUuid } },
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useActivateMapPolicy(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (policyUuid: string) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/map-policies/{policy_uuid}/activate',
        {
          params: { path: { policy_uuid: policyUuid } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useCreateMapPolicyTier(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ policyUuid, body }: { policyUuid: string; body: TierCreateBody }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/map-policies/{policy_uuid}/tiers',
        {
          params: { path: { policy_uuid: policyUuid } },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useUpdateMapPolicyTier(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ tierUuid, body }: { tierUuid: string; body: TierPatchBody }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/map-policy-tiers/{tier_uuid}',
        {
          params: { path: { tier_uuid: tierUuid } },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useDeleteMapPolicyTier(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (tierUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/map-policy-tiers/{tier_uuid}', {
        params: { path: { tier_uuid: tierUuid } },
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

// ---- Policy documents (S3-backed, version history) ----

export function usePolicyDocuments(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.manufacturers.policyDocs(manuUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/manufacturers/{uuid}/policy-documents',
        { params: { path: { uuid: manuUuid } } },
      )
      if (error) throw error
      return data
    },
    enabled: !!manuUuid,
  })
}

export function useUploadPolicyDocument(manuUuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, notes }: { file: File; notes?: string }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams()
      if (notes) params.set('notes', notes)
      const url = `${DOCS_API_BASE_URL}/dashboard/manufacturers/${manuUuid}/policy-documents${
        params.toString() ? `?${params}` : ''
      }`
      const formData = new FormData()
      formData.append('file', file)
      const resp = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!resp.ok) {
        throw await responseError(resp, `Upload failed: ${resp.status}`)
      }
      return (await resp.json()) as components['schemas']['ManufacturerPolicyDocumentRead']
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.policyDocs(manuUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manuUuid) })
    },
  })
}

export function useSetCurrentPolicyDocument(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (docUuid: string) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/policy-documents/{doc_uuid}/set-current',
        { params: { path: { doc_uuid: docUuid } } },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.policyDocs(manuUuid) })
    },
  })
}

export function useDeletePolicyDocument(manuUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (docUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE(
        '/dashboard/policy-documents/{doc_uuid}',
        { params: { path: { doc_uuid: docUuid } } },
      )
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.policyDocs(manuUuid) })
    },
  })
}

// ---- Per-product MAP/NET overrides ----

export function useSetProductMapOverride(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      map_price: string
      effective_date?: string | null
      expiration_date?: string | null
      source?: string
      notes?: string | null
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/products/{uuid}/map-override', {
        params: { path: { uuid: productUuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: body as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  })
}

export function useDeleteProductMapOverride(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/products/{uuid}/map-override', {
        params: { path: { uuid: productUuid } },
      })
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  })
}

export function useAssignProductTier(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (tierUuid: string | null) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/products/{uuid}/map-tier', {
        params: { path: { uuid: productUuid } },
        body: { tier_uuid: tierUuid },
      })
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  })
}

export function useSetProductManualNet(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { manual_net_price: string | null; reason?: string | null }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/products/{uuid}/manual-net', {
        params: { path: { uuid: productUuid } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: body as any,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  })
}

export function useProductSpecialNetHistory(productUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.specialNetHistory(productUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/special-net-history', {
        params: { path: { uuid: productUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useProductPriceHistory(productUuid: string, from: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.priceHistory(productUuid, from),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/price-history', {
        params: { path: { uuid: productUuid }, query: { from } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useProductFacts(productUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.facts(productUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/attributes', {
        params: { path: { uuid: productUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

/** The change history of each of these facts (the three sides of a size come together). */
export function useProductFactHistories(productUuid: string, keys: readonly string[]) {
  const getClient = useAuthenticatedClient()
  return useQueries({
    queries: keys.map((key) => ({
      queryKey: queryKeys.products.factHistory(productUuid, key),
      queryFn: async () => {
        const client = await getClient()
        const { data, error } = await client.GET(
          '/dashboard/products/{uuid}/attributes/{key}/history',
          { params: { path: { uuid: productUuid, key } } },
        )
        if (error) throw error
        return data
      },
    })),
  })
}

/** Saves or releases staff corrections; the response is the product's re-resolved facts. */
export function useCorrectProductFacts(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      change:
        | { kind: 'correct'; body: components['schemas']['ProductFactCorrectionWrite'] }
        | { kind: 'release'; keys: string[] },
    ) => {
      const client = await getClient()
      const { data, error } =
        change.kind === 'correct'
          ? await client.PUT('/dashboard/products/{uuid}/attributes', {
              params: { path: { uuid: productUuid } },
              body: change.body,
            })
          : await client.DELETE('/dashboard/products/{uuid}/attributes', {
              params: { path: { uuid: productUuid }, query: { keys: change.keys } },
            })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.products.facts(productUuid), data)
      // The corrected facts flow into the product, its shipping profile and the fact histories.
      void queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(productUuid) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.products.shippingFacts(productUuid) })
      void queryClient.invalidateQueries({ queryKey: ['products', 'fact-history', productUuid] })
    },
  })
}

export function useSetProductManufacturerTerms(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['ProductManufacturerTermsUpdate']) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/products/{uuid}/manufacturer-terms', {
        params: { path: { uuid: productUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  })
}

// ---- Contacts ----

export function useContacts(params?: PaginatedParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.contacts.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/contacts', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

export function useContact(contactId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.contacts.detail(contactId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/contacts/{contact_id}', {
        params: { path: { contact_id: contactId } },
      })
      if (error) throw error
      return data
    },
    enabled: !!contactId,
    refetchInterval: POLL_SLOW,
  })
}

export function useUpdateContact(contactId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const detailKey = queryKeys.contacts.detail(contactId)
  return useMutation({
    mutationFn: async (body: { name?: string; email?: string; phone?: string | null }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/contacts/{contact_id}', {
        params: { path: { contact_id: contactId } },
        body,
      })
      if (error) throw error
      return data
    },
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData(detailKey)
      queryClient.setQueryData(detailKey, (old: Record<string, unknown> | undefined) =>
        old ? { ...old, ...body } : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all })
      queryClient.invalidateQueries({ queryKey: detailKey })
      // Sourcing lists show the contact's phone (USA-217).
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all })
    },
  })
}

export function useDeleteContact(contactId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/contacts/{contact_id}', {
        params: { path: { contact_id: contactId } },
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all })
    },
  })
}

// ---- Products ----

interface ProductParams extends PaginatedParams {
  manufacturer?: string
  has_price?: boolean
  on_shopify?: boolean
  discontinued?: boolean
  configurable?: boolean
}

export function useProducts(params?: ProductParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

// Quote-tracked products the storefront search doesn't show (USA-1109).
export function useQuotableProducts(q: string, limit: number, enabled = true) {
  const getClient = useAuthenticatedClient()
  const query = q.trim()
  return useQuery({
    queryKey: queryKeys.products.quotable(query, limit),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/quotable', {
        params: { query: { q: query, limit } },
      })
      if (error) throw error
      return data
    },
    enabled: enabled && Boolean(query),
    staleTime: 30_000,
    // "Show more" keeps the shown products; a new search never shows the previous one's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === query ? previous : undefined,
  })
}

export function useProduct(uuid: string, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error, response } = await client.GET('/dashboard/products/{uuid}', {
        params: { path: { uuid } },
      })
      if (response.status === 404) return null
      if (error) throw error
      return data
    },
    enabled: !!uuid && enabled,
    // Follow a price push to Shopify closely until it lands (USA-1671).
    refetchInterval: (query) =>
      query.state.data?.shopify_price?.status === 'updating' ? 2_000 : POLL_SLOW,
  })
}

export function useProductShippingFacts(uuid: string, enabled = true, policyId?: number | null) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...queryKeys.products.shippingFacts(uuid), policyId],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/shipping-facts', {
        params: { path: { uuid }, query: { policy_id: policyId ?? undefined } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid && enabled,
    refetchInterval: POLL_SLOW,
  })
}

export function useProductShippingQuote(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['ProductShippingQuoteRequest']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/products/{uuid}/shipping-quote', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
  })
}

export function useProductDiscontinuation(uuid: string, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.discontinuation(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/discontinuation', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid && enabled,
    refetchInterval: (query) => {
      const state = query.state.data
      return state?.shopify_linked && !state.shopify_synced && state.shopify_sync_status !== 'failed'
        ? 2000
        : POLL_SLOW
    },
  })
}

export function useSetProductDiscontinued(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (discontinued: boolean) => {
      const client = await getClient()
      const request = discontinued
        ? client.POST('/dashboard/products/{uuid}/discontinue', {
            params: { path: { uuid } },
          })
        : client.POST('/dashboard/products/{uuid}/restore', {
            params: { path: { uuid } },
          })
      const { data, error } = await request
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.products.discontinuation(uuid), data)
      queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(uuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all })
    },
  })
}

export function useRetryProductDiscontinuationSync(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/products/{uuid}/discontinuation/retry',
        { params: { path: { uuid } } },
      )
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.products.discontinuation(uuid), data)
    },
  })
}

export function useProductPricingDetail(uuid: string, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.pricingDetail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/products/{uuid}/pricing-detail',
        { params: { path: { uuid } } },
      )
      if (error) throw error
      return data
    },
    enabled: enabled && !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

export function useProductConfigAccessoryV4(uuid: string, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.configAccessoryV4(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/products/{uuid}/config-accessory-v4',
        { params: { path: { uuid } } },
      )
      if (error) throw error
      return data
    },
    enabled: enabled && !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

/** The product's cleaned options: versions to switch between, its own choices and add-ons. */
export function useProductOptions(uuid: string, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.options(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/options', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data ?? null
    },
    enabled: enabled && !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

export function useProductSuggestions(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.products.suggestions(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/suggestions-preview', {
        params: { path: { uuid }, query: { limit: 6 } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

// ---- Project Documents ----

export function useUploadProjectDocument(projectUuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (files: File[]) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')

      const results = []
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        const resp = await fetch(
          `${DOCS_API_BASE_URL}/dashboard/projects/${projectUuid}/documents`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          },
        )
        if (!resp.ok) throw new Error(`Upload failed: ${file.name}`)
        results.push(await resp.json())
      }
      return results
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(projectUuid) })
    },
  })
}

export function useDeleteProjectDocument(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (docUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/dashboard/projects/{uuid}/documents/{doc_uuid}', {
        params: { path: { uuid: projectUuid, doc_uuid: docUuid } },
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(projectUuid) })
    },
  })
}

export function useSnapshotProjectDocumentUrl(projectUuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      url: string
      document_type: 'quote' | 'amendment' | 'freight' | 'project' | 'supporting'
      display_name?: string | null
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const response = await fetch(
        `${DOCS_API_BASE_URL}/dashboard/projects/${projectUuid}/documents/from-url`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      if (!response.ok) {
        throw await responseError(response, `Document snapshot failed (${response.status})`)
      }
      return response.json() as Promise<components['schemas']['ProjectDocumentRead']>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(projectUuid) })
    },
  })
}

// ---- Communications ----

export {
  useProjectCommunications,
  usePOCommunications,
  usePOInvoiceCommunications,
  useApInvoiceCommunications,
  useMessageAttachments,
  useComposeMessage,
  useSendReply,
  useForwardMessage,
  useRecordCommunication,
  useUpdateMessage,
  useUpdateThreadFollowUp,
  useApproveMessage,
  useRejectMessage,
  useSendMessage,
  useRetryMessage,
  useRetryAnalysis,
  useUpdateThreadStatus,
  useDefaultPORecipients,
  useCreatePOThread,
  useUpdateThreadRecipients,
  useCloseThread,
  useReopenThread,
  useSendPOEmails,
} from '@/features/communications/hooks'

type FollowUpUpdate = { clear?: boolean; follow_up_at?: string | null; max_follow_ups?: number }

export function useUpdateCustomerThreadFollowUp() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ threadId, body }: { threadId: number; body: FollowUpUpdate }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/customer-communications/{thread_id}/follow-up',
        {
          params: { path: { thread_id: threadId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

// ---- AI Agents ----

export function useAIAgents() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.aiAgents.list(),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/ai-agents')
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

export function useAIAgent(key: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.aiAgents.detail(key),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/ai-agents/{key}', {
        params: { path: { key } },
      })
      if (error) throw error
      return data
    },
    enabled: !!key,
    refetchInterval: POLL_SLOW,
  })
}

export function useUpdateAIAgent(key: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { system_prompt?: string; model_name?: string }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/ai-agents/{key}', {
        params: { path: { key } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.aiAgents.detail(key), data)
      queryClient.invalidateQueries({ queryKey: queryKeys.aiAgents.detail(key) })
      queryClient.invalidateQueries({ queryKey: queryKeys.aiAgents.list() })
    },
  })
}

// ---- Project Comments ----

export function useProjectComments(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.comments.list(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/projects/{uuid}/comments', {
        params: { path: { uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!projectUuid,
    refetchInterval: POLL_NORMAL,
  })
}

export function useCreateComment(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      content: string
      mentions: Array<{ entity_type: string; entity_uuid: string; display_text: string }>
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/projects/{uuid}/comments', {
        params: { path: { uuid: projectUuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.list(projectUuid) })
    },
  })
}

export function useUpdateComment(projectUuid: string) {
  type CommentRead = components['schemas']['ProjectCommentRead']
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const listKey = queryKeys.comments.list(projectUuid)
  return useMutation({
    mutationFn: async ({ commentUuid, content }: { commentUuid: string; content: string }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/projects/{uuid}/comments/{comment_uuid}',
        {
          params: { path: { uuid: projectUuid, comment_uuid: commentUuid } },
          body: { content },
        },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ commentUuid, content }) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const previous = queryClient.getQueryData<CommentRead[]>(listKey)
      queryClient.setQueryData<CommentRead[]>(listKey, (old) =>
        old?.map((c) =>
          c.uuid === commentUuid ? { ...c, content, updated_at: new Date().toISOString() } : c,
        ),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey })
    },
  })
}

export function useDeleteComment(projectUuid: string) {
  type CommentRead = components['schemas']['ProjectCommentRead']
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const listKey = queryKeys.comments.list(projectUuid)
  return useMutation({
    mutationFn: async (commentUuid: string) => {
      const client = await getClient()
      const { error } = await client.DELETE(
        '/dashboard/projects/{uuid}/comments/{comment_uuid}',
        {
          params: { path: { uuid: projectUuid, comment_uuid: commentUuid } },
        },
      )
      if (error) throw error
    },
    onMutate: async (commentUuid) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const previous = queryClient.getQueryData<CommentRead[]>(listKey)
      queryClient.setQueryData<CommentRead[]>(listKey, (old) =>
        old?.filter((c) => c.uuid !== commentUuid),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey })
    },
  })
}

// ---- Staff ----

interface StaffParams {
  search?: string
  role?: string
  active?: boolean
}

export function useStaff(params?: StaffParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.staff.list(params),
    queryFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        const staff: components['schemas']['UserRead'][] = [
          MOCK_CURRENT_USER,
          {
            id: 1002,
            uuid: 'staff-jamie-ops',
            user_id: 'jamie.ops@shopusars.com',
            email: 'jamie.ops@shopusars.com',
            name: 'Jamie Operations',
            role: 'operations',
          },
          {
            id: 1003,
            uuid: 'staff-taylor-sales',
            user_id: 'taylor.sales@shopusars.com',
            email: 'taylor.sales@shopusars.com',
            name: 'Taylor Sales',
            role: 'sales',
          },
        ]
        const search = params?.search?.trim().toLowerCase()
        const role = params?.role
        return staff.filter(
          (user) =>
            (!role || user.role === role) &&
            (!search || user.name.toLowerCase().includes(search) || user.email.toLowerCase().includes(search)),
        )
      }
      const client = await getClient()
      const { search, role } = params ?? {}
      const { data, error } = await client.GET('/dashboard/staff', {
        params: { query: { search, role } },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_SLOW,
  })
}

export function useStaffMember(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.staff.detail(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/staff/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_SLOW,
  })
}

export function useUpdateStaffMember(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['UserUpdate']) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/staff/{uuid}', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.detail(uuid) })
    },
  })
}

/** Set or remove a staff member's profile photo (USA-1456). */
export function useStaffPhoto(uuid: string) {
  const { getAccessToken } = useAuth()
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File | null) => {
      if (!file) {
        const client = await getClient()
        const { data, error } = await client.DELETE('/dashboard/staff/{uuid}/photo', {
          params: { path: { uuid } },
        })
        if (error) throw error
        return data
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const formData = new FormData()
      formData.append('file', file)
      const resp = await fetch(`${DOCS_API_BASE_URL}/dashboard/staff/${uuid}/photo`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!resp.ok) {
        throw await responseError(resp, 'Photo upload failed')
      }
      return (await resp.json()) as components['schemas']['UserDetailRead']
    },
    onSuccess: (user) => {
      queryClient.setQueryData(queryKeys.staff.detail(uuid), user)
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all })
    },
  })
}

export function useStaffProjects(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.staff.projects(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/staff/{uuid}/projects', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!uuid,
    refetchInterval: POLL_NORMAL,
  })
}

// ---- Project role defaults ----

export type ProjectRoleDefault = components['schemas']['ProjectRoleDefaultRead']
export type ProjectRoleDefaultField =
  | 'sales_person_id'
  | 'ops_person_id'
  | 'warehousing_person_id'
  | 'accounting_person_id'
type ProjectRoleDefaultUpdate = components['schemas']['ProjectRoleDefaultUpdate']
type ProjectType = ProjectRoleDefault['project_type']

export function useProjectRoleDefaults() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.projectRoleDefaults.all,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/project-role-defaults')
      if (error) throw error
      return data
    },
  })
}

export function useUpdateProjectRoleDefaults() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      projectType,
      body,
    }: {
      projectType: ProjectType
      field: ProjectRoleDefaultField
      body: ProjectRoleDefaultUpdate
    }) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/project-role-defaults/{project_type}', {
        params: { path: { project_type: projectType } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<ProjectRoleDefault[]>(queryKeys.projectRoleDefaults.all, (current) =>
        current?.map((defaults) =>
          defaults.project_type === updated.project_type ? updated : defaults,
        ),
      )
    },
  })
}

// ---- Mention Search ----

export function useMentionSearch(query: string, projectUuid?: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.mentions.search(query, projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/mentions/search', {
        params: {
          query: { q: query, project_uuid: projectUuid, limit: 10 },
        },
      })
      if (error) throw error
      return data
    },
    enabled: query.length >= 2,
  })
}

// ---- Global Search ----

export function useGlobalSearch(query: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.globalSearch.query(query),
    queryFn: async ({ signal }) => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/search', {
        params: { query: { q: query, limit: 5 } },
        // A newer keystroke cancels the request it replaces.
        signal,
      })
      if (error) throw error
      return data
    },
    enabled: query.length >= 2,
    staleTime: 60_000,
    // Keep the last results on screen while the next query loads.
    placeholderData: keepPreviousData,
  })
}

// ---- Customer Communications ----
// NOTE: These hooks use `as any` casts because the OpenAPI schema hasn't been
// regenerated yet. Once `schema.json` is updated and `openapi-typescript` is
// re-run, remove the casts and let the generated types flow through.

interface CustomerCommListParams extends PaginatedParams {
  status?: string
  channel?: string
  project_id?: number
  order_id?: number
  customer_group_id?: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = { GET: any; POST: any; PATCH: any }

export function useCustomerCommThreads(params?: CustomerCommListParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerComms.list(params),
    queryFn: async () => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.GET('/dashboard/customer-communications', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useCustomerCommThread(threadId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerComms.detail(threadId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/customer-communications/{thread_id}',
        {
          params: { path: { thread_id: threadId } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!threadId,
    // While the AI works on a message, check back often so its result lands without a reload.
    refetchInterval: (query) =>
      query.state.data?.analysis.some((a) => a.state === 'queued' || a.state === 'running')
        ? POLL_ANALYSIS
        : POLL_NORMAL,
  })
}

export function useCustomerCommStats() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerComms.stats,
    queryFn: async () => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.GET('/dashboard/customer-communications/stats', {})
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useCustomerCommActions(params?: {
  status?: string
  action_type?: string
  page?: number
  limit?: number
}) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerComms.actions(params),
    queryFn: async () => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.GET('/dashboard/customer-communications/actions', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useCustomerCommsByProject(projectId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerComms.byProject(projectId),
    queryFn: async () => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.GET(
        '/dashboard/customer-communications/by-project/{project_id}',
        {
          params: { path: { project_id: projectId } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!projectId,
    refetchInterval: POLL_NORMAL,
  })
}

export function useCustomerCommsByOrder(orderId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.customerComms.byOrder(orderId),
    queryFn: async () => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.GET(
        '/dashboard/customer-communications/by-order/{order_id}',
        {
          params: { path: { order_id: orderId } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!orderId,
    refetchInterval: POLL_NORMAL,
  })
}

export function useUpdateCustomerCommThreadStatus(threadId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (status: string) => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.PATCH(
        '/dashboard/customer-communications/{thread_id}',
        {
          params: { path: { thread_id: threadId }, query: { status } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

export function useAddCustomerCommMessage(threadId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      message_type?: string
      subject?: string
      body_text: string
      body_html?: string
    }) => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.POST(
        '/dashboard/customer-communications/{thread_id}/messages',
        {
          params: { path: { thread_id: threadId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

export function useSendCustomerCommMessage(threadId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (messageId: number) => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.POST(
        '/dashboard/customer-communications/{thread_id}/messages/{message_id}/send',
        {
          params: { path: { thread_id: threadId, message_id: messageId } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

export function useSendCustomerReply(threadId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { body_text: string }) => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.POST(
        '/dashboard/customer-communications/{thread_id}/reply',
        {
          params: { path: { thread_id: threadId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

export function useRecordCustomerComm(threadId: number) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      body_text: string
      message_type?: string
      recorded_by?: string
      source?: string
    }) => {
      const client = (await getClient()) as unknown as AnyClient
      const { data, error } = await client.POST(
        '/dashboard/customer-communications/{thread_id}/record',
        {
          params: { path: { thread_id: threadId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

export function useRetryShipmentBooking(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (shipmentId: number) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/retry-booking',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
    },
  })
}

// ---- Manual Shipment Booking ----

export function useBookShipment(poUuid: string, shipmentUuid?: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (shipmentId: number) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/book',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      if (shipmentUuid) {
        queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(shipmentUuid) })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

// ---- Record a booking made by hand (WWEX portal, UPS.com, a carrier) on a shipment (USA-1455) ----

export type RecordHandBookingBody = components['schemas']['RecordHandBookingRequest']

export function useRecordHandBooking(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ shipmentId, body }: { shipmentId: number; body: RecordHandBookingBody }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/record-booking',
        { params: { path: { uuid: poUuid, shipment_id: shipmentId } }, body },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.globalActions })
    },
  })
}

// ---- Create Shipment (manual entry: external-recorded OR manual book via our system) ----

export function useCreateShipment(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateShipmentRequest']) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments',
        {
          params: { path: { uuid: poUuid } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

// ---- Edit Shipment (only for non-booked shipments) ----

export function useEditShipment(poUuid: string, shipmentUuid?: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      shipmentId,
      body,
    }: {
      shipmentId: number
      body: components['schemas']['EditShipmentRequest']
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      if (shipmentUuid) {
        queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(shipmentUuid) })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

export function useCorrectShipmentStatus(poUuid: string, shipmentUuid?: string) {
  const getClient = useAuthenticatedClient()
  const invalidate = useStatusCorrectionInvalidation()
  const queryClient = useQueryClient()
  const poDetailKey = queryKeys.purchaseOrders.detail(poUuid)
  const shipmentDetailKey = shipmentUuid ? queryKeys.shipments.detail(shipmentUuid) : null
  return useMutation({
    mutationFn: async ({
      shipmentId,
      body,
    }: {
      shipmentId: number
      body: ShipmentStatusCorrectionBody
    }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/status-correction',
        { params: { path: { uuid: poUuid, shipment_id: shipmentId } }, body },
      )
      if (error) throw error
      return data
    },
    onMutate: async ({ shipmentId, body }) => {
      await queryClient.cancelQueries({ queryKey: poDetailKey })
      if (shipmentDetailKey) await queryClient.cancelQueries({ queryKey: shipmentDetailKey })
      const previousPO = queryClient.getQueryData<components['schemas']['PurchaseOrderDetailRead']>(poDetailKey)
      const previousShipment = shipmentDetailKey
        ? queryClient.getQueryData<components['schemas']['PurchaseOrderShipmentDetailRead']>(shipmentDetailKey)
        : undefined
      queryClient.setQueryData<components['schemas']['PurchaseOrderDetailRead']>(poDetailKey, (current) =>
        current
          ? {
              ...current,
              shipments: current.shipments.map((shipment) =>
                shipment.id === shipmentId ? withOptimisticShipment(shipment, body) : shipment,
              ),
            }
          : current,
      )
      if (shipmentDetailKey) {
        queryClient.setQueryData<components['schemas']['PurchaseOrderShipmentDetailRead']>(
          shipmentDetailKey,
          (current) => (current ? withOptimisticShipment(current, body) : current),
        )
      }
      return { previousPO, previousShipment }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousPO) queryClient.setQueryData(poDetailKey, context.previousPO)
      if (shipmentDetailKey && context?.previousShipment) {
        queryClient.setQueryData(shipmentDetailKey, context.previousShipment)
      }
    },
    onSettled: () => {
      invalidate()
      if (shipmentUuid) queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(shipmentUuid) })
    },
  })
}

// ---- Shipment Cancellation ----

export function useCancelShipment(poUuid: string, shipmentUuid?: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ shipmentId, reason }: { shipmentId: number; reason: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/cancel',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
          body: { reason },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      if (shipmentUuid) {
        queryClient.invalidateQueries({ queryKey: queryKeys.shipments.detail(shipmentUuid) })
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

// ---- Shipment Rebooking ----

export function useRebookShipment(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      shipmentId,
      body,
    }: {
      shipmentId: number
      body: components['schemas']['RebookShipmentRequest']
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/rebook',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
          body,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

// ---- Shipment Duplication ----

export function useDuplicateShipment(poUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ shipmentId }: { shipmentId: number }) => {
      const client = await getClient()
      const { data, error } = await client.POST(
        '/dashboard/purchase-orders/{uuid}/shipments/{shipment_id}/duplicate',
        {
          params: { path: { uuid: poUuid, shipment_id: shipmentId } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.detail(poUuid) })
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.all })
    },
  })
}

// ---- E2E Testing ----

export function useE2EScenarios() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.e2eTesting.scenarios,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/e2e/scenarios')
      if (error) throw error
      return data
    },
  })
}

export function useE2EStatus() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.e2eTesting.status,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/e2e/status')
      if (error) throw error
      return data
    },
  })
}

export function useE2ESeed() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['SeedRequest']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/seed', { body })
      if (error) throw error
      return data
    },
  })
}

export function useE2EInjectReply() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: { purchase_order_uuid: string; scenario_key: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/inject-reply', { body })
      if (error) throw error
      return data
    },
  })
}

export function useE2EInjectSpecCreditReply() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: { purchase_order_uuid: string; scenario_key: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/inject-spec-credit-reply', {
        body,
      })
      if (error) throw error
      return data
    },
  })
}

export function useE2ESendPO() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: { purchase_order_uuid: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/send-po', { body })
      if (error) throw error
      return data
    },
  })
}

export function useE2EInjectCustomerReply() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: { thread_uuid: string; scenario_key: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/inject-customer-reply', { body })
      if (error) throw error
      return data
    },
  })
}

export function useE2ECreateCustomerThread() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateCustomerThreadRequest']) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/create-customer-thread', { body })
      if (error) throw error
      return data
    },
  })
}

export function useE2EInjectShippingReply() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: { purchase_order_uuid: string; scenario_key: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/inject-shipping-reply', { body })
      if (error) throw error
      return data
    },
  })
}

// ---- API Keys ----

export function useApiKeys() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.apiKeys.list(),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/api-keys')
      if (error) throw error
      return data
    },
  })
}

export function useCreateApiKey() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      name: string
      scopes?: string | null
      expires_at?: string | null
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/api-keys', { body })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all })
    },
  })
}

export function useRevokeApiKey() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (keyId: number) => {
      const client = await getClient()
      const { data, error } = await client.DELETE('/dashboard/api-keys/{key_id}', {
        params: { path: { key_id: keyId } },
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all })
    },
  })
}

export function useE2ECleanup() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: { project_uuid?: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/e2e/cleanup', { body })
      if (error) throw error
      return data
    },
  })
}

// ---- Manufacturer Data Imports ----

type ImportUploadResponse = components['schemas']['ImportUploadResponse']
type ActivityTaskRead = components['schemas']['ActivityTaskRead']

const IMPORTS_API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

async function uploadImportFile(
  token: string,
  path: '/dashboard/imports/discounts' | '/dashboard/imports/purchasing',
  file: File,
): Promise<ImportUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  const resp = await fetch(`${IMPORTS_API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  })
  if (!resp.ok) {
    let message = `Upload failed (${resp.status})`
    try {
      const body = await resp.json()
      if (body?.detail) message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch {
      // ignore JSON parse errors
    }
    throw new Error(message)
  }
  return (await resp.json()) as ImportUploadResponse
}

export function useImportManufacturerDiscounts() {
  const { getAccessToken } = useAuth()
  return useMutation({
    mutationFn: async (file: File) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      return uploadImportFile(token, '/dashboard/imports/discounts', file)
    },
  })
}

export function useImportPurchasingList() {
  const { getAccessToken } = useAuth()
  return useMutation({
    mutationFn: async (file: File) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      return uploadImportFile(token, '/dashboard/imports/purchasing', file)
    },
  })
}

export type SpecialPromotion = components['schemas']['SpecialPromotionRead']
export type SpecialProduct = components['schemas']['SpecialProductRead']
export type SpecialsListParams = {
  status?: 'active' | 'upcoming' | 'ended' | 'all'
  search?: string
  limit?: number
  offset?: number
}

export function useSpecials(params: SpecialsListParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.specials.list(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/specials', { params: { query: params } })
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
  })
}

export function useSpecialProducts(key: string | null, params: { search?: string; offset?: number }) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.specials.products(key ?? '', params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/specials/{key}/products', {
        params: { path: { key: key ?? '' }, query: { ...params, limit: 25 } },
      })
      if (error) throw error
      return data
    },
    enabled: key != null,
    placeholderData: keepPreviousData,
  })
}

export function useEndSpecial() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (key: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/specials/{key}/end', {
        params: { path: { key } },
      })
      if (error) throw error
      return data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.specials.all }),
  })
}

export type SpecialsImportLine = {
  line: number
  manufacturer: string
  manufacturer_found: boolean
  promotion: string | null
  term: string
  min_order_net: string | null
  min_quantity: number | null
  starts_on: string
  ends_on: string
  whole_line: boolean
  products: number
  skipped: Record<string, number>
  missing_models: string[]
}

export type SpecialsImportResult = {
  programs: string[]
  rows: number
  products: number
  special_rows: number
  unmatched_rows: number
  lines: SpecialsImportLine[]
  applied: boolean
  written?: number
}

export function useImportSpecials() {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, apply }: { file: File; apply: boolean }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const formData = new FormData()
      formData.append('file', file)
      const resp = await fetch(`${IMPORTS_API_BASE_URL}/dashboard/specials/import?apply=${apply}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!resp.ok) {
        let message = `Upload failed (${resp.status})`
        try {
          const body = await resp.json()
          if (typeof body?.detail === 'string') message = body.detail
        } catch {
          // keep the status message
        }
        throw new Error(message)
      }
      return (await resp.json()) as SpecialsImportResult
    },
    onSuccess: (result) => {
      if (result.applied) queryClient.invalidateQueries({ queryKey: queryKeys.specials.all })
    },
  })
}

export function useDownloadSpecialsTemplate() {
  const { getAccessToken } = useAuth()
  return useMutation({
    mutationFn: async () => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const resp = await fetch(`${IMPORTS_API_BASE_URL}/dashboard/specials/template.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`)
      const url = URL.createObjectURL(await resp.blob())
      const link = document.createElement('a')
      link.href = url
      link.download = 'specials-template.csv'
      link.click()
      URL.revokeObjectURL(url)
    },
  })
}

/**
 * Poll a background task by UUID until it reaches a terminal state.
 * Pass null/undefined to disable the query.
 */
export function useTaskStatus(taskUuid: string | null | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery<ActivityTaskRead>({
    queryKey: ['task-status', taskUuid],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/activity/tasks/{task_uuid}', {
        params: { path: { task_uuid: taskUuid! } },
      })
      if (error) throw error
      return data
    },
    enabled: !!taskUuid,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'completed' || status === 'failed') return false
      return 2000
    },
  })
}
